import { ChevronLeftOutlineIcon, ChevronRightOutlineIcon } from '@ikyucom/design-system-icons-react'
import { atomWithQuery } from 'jotai-tanstack-query'
import { atom, useAtomValue } from 'jotai/index'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { FC, MouseEvent } from 'react'
import Image from '~/components/image/Image'
import { KiwaminoArticleDialog } from '~/features/kiwamino/KiwaminoArticleDialog'
import {
  closeDialogAfterAnimation,
  openDialogWithAnimation,
} from '~/features/kiwamino/lib/animation'
import type { FragmentType } from '~/lib/graphql'
import { getFragmentData, graphql } from '~/lib/graphql'
import type { KiwaminoArticleId, RestaurantId } from '~/lib/graphql/scalars'
import { useInitAtom } from '~/lib/jotai'
import { useIsPC } from '~/lib/ua'
import { cva, sva } from '~/styled-system/css'

export const KiwaminoArticleInfo = graphql(`
  fragment KiwaminoArticleInfo on KiwaminoArticle {
    articleId
    title
    content
    postedAt
    thumbnailUrl
  }
`)

// FIXME ガイド各所で使えるので移動したい
// hydrate で初期化するため、undefined で初期化
export const restaurantId$ = atom<RestaurantId>(undefined as unknown as RestaurantId)

export function useInitRestaurantId(restaurantId: RestaurantId) {
  useInitAtom(restaurantId$, restaurantId)
}

const articles$ = atomWithQuery<FragmentType<typeof KiwaminoArticleInfo>[]>((get) => {
  return {
    queryKey: ['kiwamino-articles', get(restaurantId$)],
    // loader から hydration で得るので空でよい
    queryFn: async () => [],
    gcTime: 1000 * 60 * 60 * 24,
    staleTime: 1000 * 60 * 60 * 24,
  }
})

export const IkyuOnlyKiwaminoArticles: FC =
  import.meta.env.VITE_MODE === 'ppg' ? EmptyKiwaminoArticles : KiwaminoArticles

function EmptyKiwaminoArticles() {
  return null
}

const bodyStyles = sva({
  slots: [
    'body',
    'title',
    'container',
    'contents',
    'button',
    'image',
    'articleItem',
    'articleTitle',
    'chevron',
  ],
  base: {
    body: {
      width: '[100%]',
      spaceY: '3',
    },
    title: {
      fontSize: 'lg',
      fontWeight: 'bold',
      _pc: {
        fontSize: '3xl',
      },
      _sd: {
        px: '3',
      },
    },
    container: {
      position: 'relative',
    },
    contents: {
      display: 'flex',
      alignItems: 'center',
      gap: '2',
      overflowX: 'auto',
      paddingBottom: '2',
      paddingRight: '3',
      _pc: {
        p: '0',
        position: 'relative',
      },
    },
    button: {
      display: 'flex',
      width: '[18rem]',
      flexShrink: 0,
      flexDirection: 'row',
      gap: '2',
      borderRadius: 'md',
      backgroundColor: 'white',
      _sd: {
        '&:first-of-type': { marginLeft: '3' },
        alignItems: 'center',
        boxShadow: 'outline.sm',
      },
    },
    image: {
      borderRadius: '[4px 0 0 4px]',
      objectFit: 'cover',
      _pc: {
        borderRadius: 'md',
      },
    },
    articleItem: {
      display: 'flex',
      flex: '1',
      alignItems: 'center',
      paddingRight: '3',
      textAlign: 'left',
    },
    articleTitle: {
      display: '-webkit-box',
      fontSize: 'sm',
      lineClamp: 2,
      _pc: {
        color: 'link',
        lineClamp: 3,
      },
    },
    chevron: {
      width: '[20px]',
      height: '[20px]',
    },
  },
})

const scrollStyles = cva({
  base: {
    position: 'absolute',
    top: '[calc(50% - 16px)]',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'full',
    borderWidth: 'thin',
    borderColor: 'gray.300',
    backgroundColor: 'white',
    color: 'tertiary',
    width: '[32px]',
    height: '[32px]',
  },
  variants: {
    direction: {
      prev: {
        left: '[12px]',
      },
      next: {
        right: '[12px]',
      },
    },
  },
})

function KiwaminoArticles() {
  const { data: fragment } = useAtomValue(articles$)
  if (fragment === null || fragment === undefined) {
    return null
  }
  return <Body fragment={fragment} />
}

function Body({ fragment }: { fragment: FragmentType<typeof KiwaminoArticleInfo>[] }) {
  const articleContainer = useRef<HTMLDivElement>(null)
  const isPc = useIsPC()
  const imageSize = isPc ? 80 : 75
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showNext, setShowNext] = useState(false)
  const [showPrev, setShowPrev] = useState(false)

  useEffect(() => {
    const ref = scrollRef.current
    if (ref === null) {
      return
    }
    const first = ref.querySelector(`#kiwamino-${0}`)
    const last = ref.querySelector(`#kiwamino-${fragment.length - 1}`)
    if (first === null || last === null) {
      return
    }
    const firstObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          setShowPrev(!entry.isIntersecting)
        }
      },
      {
        root: ref,
        threshold: 1,
      }
    )
    const lastObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          setShowNext(!entry.isIntersecting)
        }
      },
      {
        root: ref,
        threshold: 1,
      }
    )
    firstObserver.observe(first)
    lastObserver.observe(last)
    return () => {
      firstObserver.disconnect()
      lastObserver.disconnect()
    }
  }, [fragment])

  const onClickChevron = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    const type = e.currentTarget.dataset.type
    const ref = scrollRef.current
    if (ref === null) {
      return
    }
    if (type === 'next') {
      ref.scrollTo({
        left: ref.scrollLeft + ref.clientWidth,
        behavior: 'smooth',
      })
    }
    if (type === 'prev') {
      ref.scrollTo({
        left: ref.scrollLeft - ref.clientWidth,
        behavior: 'smooth',
      })
    }
  }, [])

  const openArticle = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const articleId = e.currentTarget.dataset.articleId
      const currentArticle = articleContainer.current?.querySelector(
        `dialog[data-article-id="${articleId}"]`
      ) as HTMLDialogElement | null | undefined
      const contents = articleContainer.current?.querySelector(
        `div[data-article-id="${articleId}"]`
      ) as HTMLDivElement | null | undefined
      if (!currentArticle || !contents) {
        return
      }
      openDialogWithAnimation(currentArticle, contents, isPc)
    },
    [isPc]
  )

  const switchArticle = useCallback(
    async (newArticleId: KiwaminoArticleId) => {
      // ふすまが閉じる様子がユーザーに見えないよう、先に新しい記事を開き、その裏で古い記事を閉じる
      const article = articleContainer.current?.querySelector(
        `dialog[data-article-id="${newArticleId}"]`
      ) as HTMLDialogElement | null | undefined
      const contents = articleContainer.current?.querySelector(
        `div[data-article-id="${newArticleId}"]`
      ) as HTMLDivElement | null | undefined
      if (!article || !contents) {
        return
      }
      openDialogWithAnimation(article, contents, isPc)
      const opened = articleContainer.current?.querySelector(
        `dialog[open]:not([data-article-id="${newArticleId}"])`
      ) as HTMLDialogElement | null | undefined
      if (opened) {
        await closeDialogAfterAnimation(opened, isPc)
      }
    },
    [isPc]
  )

  const articles = getFragmentData(KiwaminoArticleInfo, fragment)
  if (articles.length === 0) {
    return null
  }

  const classes = bodyStyles()

  return (
    <div className={classes.body}>
      <h3 className={classes.title}>このレストランの記事</h3>
      <div className={classes.container}>
        <div className={classes.contents} ref={scrollRef}>
          {articles.map((article, i) => (
            <button
              type="button"
              data-article-id={article.articleId}
              id={`kiwamino-${i}`}
              onClick={openArticle}
              key={article.articleId}
              className={classes.button}
            >
              <Image
                src={article.thumbnailUrl}
                alt={article.title}
                className={classes.image}
                width={imageSize}
                height={imageSize}
                usePlaceholder={true}
              />
              <div className={classes.articleItem}>
                <p className={classes.articleTitle}>{article.title}</p>
              </div>
            </button>
          ))}
        </div>
        {isPc && (
          <>
            {showNext && (
              <button
                type="button"
                data-type="next"
                className={scrollStyles({ direction: 'next' })}
                onClick={onClickChevron}
              >
                <ChevronRightOutlineIcon className={classes.chevron} />
              </button>
            )}
            {showPrev && (
              <button
                type="button"
                data-type="prev"
                className={scrollStyles({ direction: 'prev' })}
                onClick={onClickChevron}
              >
                <ChevronLeftOutlineIcon className={classes.chevron} />
              </button>
            )}
          </>
        )}
      </div>
      <div ref={articleContainer}>
        {articles.map((article) => (
          <KiwaminoArticleDialog
            key={article.articleId}
            articleId={article.articleId}
            articles={fragment}
            switchArticle={switchArticle}
          />
        ))}
      </div>
    </div>
  )
}
